import { useState, useEffect, useCallback, useRef, type FC } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useWalkthrough } from "src/contexts/WalkthroughContext";
import type { WalkthroughStep } from "./walkthrough-steps";

interface WalkthroughTooltipProps {
  step: WalkthroughStep;
}

interface Position {
  top: number;
  left: number;
}

const TOOLTIP_OFFSET = 12;
const VIEWPORT_MARGIN = 16;

function getFlippedSide(
  position: WalkthroughStep["position"],
  isRTL: boolean,
): "top" | "bottom" | "left" | "right" {
  if (position === "center") return "bottom";
  if (!isRTL) return position as "top" | "bottom" | "left" | "right";
  if (position === "right") return "left";
  if (position === "left") return "right";
  return position as "top" | "bottom";
}

const WalkthroughTooltip: FC<WalkthroughTooltipProps> = ({ step }) => {
  const { t, i18n } = useTranslation("walkthrough");
  const {
    isActive,
    currentStep,
    totalSteps,
    nextStep,
    prevStep,
    skipTour,
  } = useWalkthrough();
  const [pos, setPos] = useState<Position>({ top: 0, left: 0 });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const isRTL = i18n.language.startsWith("ar");
  const side = getFlippedSide(step.position, isRTL);

  const calculatePosition = useCallback(() => {
    if (!step.targetSelector) return;

    const target = document.querySelector(step.targetSelector);
    if (!target) return;

    const rect = target.getBoundingClientRect();
    const tooltipEl = tooltipRef.current;
    const tooltipWidth = tooltipEl?.offsetWidth || 320;
    const tooltipHeight = tooltipEl?.offsetHeight || 160;

    let top = 0;
    let left = 0;

    switch (side) {
      case "top":
        top = rect.top - tooltipHeight - TOOLTIP_OFFSET;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
      case "bottom":
        top = rect.bottom + TOOLTIP_OFFSET;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
      case "left":
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.left - tooltipWidth - TOOLTIP_OFFSET;
        break;
      case "right":
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.right + TOOLTIP_OFFSET;
        break;
    }

    // Clamp to viewport
    const maxLeft = window.innerWidth - tooltipWidth - VIEWPORT_MARGIN;
    const maxTop = window.innerHeight - tooltipHeight - VIEWPORT_MARGIN;
    left = Math.max(VIEWPORT_MARGIN, Math.min(left, maxLeft));
    top = Math.max(VIEWPORT_MARGIN, Math.min(top, maxTop));

    setPos({ top, left });
  }, [step.targetSelector, side]);

  useEffect(() => {
    if (!isActive || step.type === "modal") return;
    // Delay to allow overlay animation
    const raf = requestAnimationFrame(() => calculatePosition());
    return () => cancelAnimationFrame(raf);
  }, [isActive, currentStep, calculatePosition, step.type]);

  useEffect(() => {
    if (!isActive || step.type === "modal") return;

    const handleResize = () => calculatePosition();
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleResize, true);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleResize, true);
    };
  }, [isActive, calculatePosition, step.type]);

  // Scroll target into view if needed
  useEffect(() => {
    if (!isActive || !step.targetSelector || step.type === "modal") return;

    const target = document.querySelector(step.targetSelector);
    if (!target) return;

    const rect = target.getBoundingClientRect();
    const inViewport =
      rect.top >= 0 &&
      rect.bottom <= window.innerHeight &&
      rect.left >= 0 &&
      rect.right <= window.innerWidth;

    if (!inViewport) {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
      // Recalculate after scroll
      const timer = setTimeout(calculatePosition, 400);
      return () => clearTimeout(timer);
    }
  }, [isActive, currentStep, step.targetSelector, step.type, calculatePosition]);

  if (!isActive || step.type === "modal") return null;

  const tooltip = (
    <AnimatePresence mode="wait">
      <motion.div
        key={step.id}
        ref={tooltipRef}
        className="fixed z-[10000] w-80 rounded-xl bg-white shadow-2xl border border-gray-200 overflow-hidden"
        style={{ top: pos.top, left: pos.left }}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
      >
        {/* Progress bar */}
        <div className="h-1 bg-gray-100">
          <div
            className="h-full bg-blue-500 transition-all duration-300"
            style={{
              width: `${((currentStep + 1) / totalSteps) * 100}%`,
            }}
          />
        </div>

        <div className="p-4">
          {/* Title */}
          <h3 className="text-base font-semibold text-gray-900 mb-1">
            {t(step.title)}
          </h3>

          {/* Description */}
          <p className="text-sm text-gray-600 mb-4 leading-relaxed">
            {t(step.description)}
          </p>

          {/* Footer */}
          <div className="flex items-center justify-between" dir={isRTL ? "rtl" : "ltr"}>
            {/* Progress text */}
            <span className="text-xs text-gray-400">
              {t("progress", {
                current: currentStep + 1,
                total: totalSteps,
              })}
            </span>

            {/* Navigation */}
            <div className="flex items-center gap-1">
              <button
                onClick={skipTour}
                className="px-2 py-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
              >
                {t("navigation.skip")}
              </button>

              {currentStep > 0 && (
                <button
                  onClick={prevStep}
                  className="inline-flex items-center justify-center h-7 w-7 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                  aria-label={t("navigation.previous")}
                >
                  {isRTL ? (
                    <ChevronRight className="h-4 w-4" />
                  ) : (
                    <ChevronLeft className="h-4 w-4" />
                  )}
                </button>
              )}

              <button
                onClick={nextStep}
                className="inline-flex items-center justify-center rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 transition-colors"
              >
                {t("navigation.next")}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );

  return createPortal(tooltip, document.body);
};

export default WalkthroughTooltip;
